import { useNavigate } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatErr, inr } from "@/lib/api";
import { createKeyboardNav, backspacePrev, arrowNav, handleSimpleFormNav } from "@/lib/keyboardNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Pencil, Printer, Wallet, Layers, ArrowRight, ArrowLeft, CornerDownLeft, Minimize2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useMinimized } from "@/context/MinimizedContext";
import { ItemCombobox } from "@/components/ItemCombobox";
import { PaymentPanel, openTxnPdf } from "@/components/PaymentPanel";
import { ItemRegisterView } from "@/components/ItemRegisterView";
import { ReorderRecords } from "@/components/ReorderRecords";
import { useI18n } from "@/context/I18nContext";
import { PaginationBar, usePagination } from "@/components/Pagination";

const todayISO = () => new Date().toISOString().slice(0, 10);
const round2 = (n) => Math.round(n * 100) / 100;
const statusColor = {
  Unpaid: "text-red-500 bg-red-500/10 border-red-500/40",
  "Partially Paid": "text-yellow-500 bg-yellow-500/10 border-yellow-500/40",
  Paid: "text-green-500 bg-green-500/10 border-green-500/40",
};

const emptyBatch = { batch_number: "", qty: 1, mrp: 0, per_unit_cost: 0, cgst: 9, sgst: 9, igst: 0, expiry_date: "", serial_numbers: [] };
const emptyLine = { item_id: "", qty: 1, rate: 0, discount_pct: 0, cgst: 9, sgst: 9, igst: 0, batches: [], batches_filled: false };
const emptyForm = () => ({ party_id: "", party_name: "", party_phone: "", party_address: "", invoice_number: "", tax_type: "GST", bill_calc: "Exclusive", store_id: "", txn_date: todayISO(), rate_type: "purchase_rate", lines: [{ ...emptyLine }], overall_discount: 0, overall_discount_type: "amt", notes: "" });

export default function PurchaseTransactions({ type, label, onFullScreenChange }) {
  const navigate = useNavigate();
  const isRetail = type === "retail_purchase";
  const { user } = useAuth();
  const { settings: appSettings } = useI18n();
  const { minimize, consumeRestore, pending } = useMinimized();
  const canModify = ["owner", "manager"].includes(user.role);
  const [list, setList] = useState([]);
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [stores, setStores] = useState([]);
  const [dateFrom, setDateFrom] = useState(todayISO());
  const [dateTo, setDateTo] = useState(todayISO());
  const [q, setQ] = useState("");
  const pg = usePagination(list, q);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const formRef = useRef(form); formRef.current = form;
  const [batchIdx, setBatchIdx] = useState(null);
  const [supInfo, setSupInfo] = useState(null);
  const [addSupOpen, setAddSupOpen] = useState(false);
  const [sup, setSup] = useState({ name: "", phone: "", gstin: "", payment_terms: "", address: "" });
  const [payDlg, setPayDlg] = useState(null);
  const [payForm, setPayForm] = useState({ amount: 0, method: "cash", note: "", paid_on: todayISO() });
  const [payments, setPayments] = useState([]);
  const [payOpen, setPayOpen] = useState(false);
  const [regItemId, setRegItemId] = useState(null);
  const [savedDoc, setSavedDoc] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    const p = new URLSearchParams({ type });
    if (dateFrom) p.set("date_from", dateFrom);
    if (dateTo) p.set("date_to", dateTo);
    if (q) p.set("q", q);
    api.get(`/transactions?${p.toString()}`).then(r => setList(r.data)).catch(() => {});
  };
  const loadItems = () => api.get("/items?scope=all").then(r => setItems(r.data)).catch(() => {});
  const loadVendors = () => api.get("/vendors").then(r => setVendors(r.data)).catch(() => {});
  useEffect(() => { load(); }, [type, dateFrom, dateTo, q]); // eslint-disable-line
  useEffect(() => { onFullScreenChange?.(open); }, [open]); // eslint-disable-line
  const prevOpen = useRef(false);
  const prevBatch = useRef(null);
  useEffect(() => {
    const justOpened = open && !prevOpen.current;
    const enteredBatch = open && batchIdx !== null && prevBatch.current === null;
    prevOpen.current = open;
    prevBatch.current = batchIdx;
    if (!justOpened && !enteredBatch) return;
    const t = setTimeout(() => {
      const container = document.querySelector('[data-testid="purchase-form-page"]');
      if (!container) return;
      const first = container.querySelector('[role="combobox"], input:not([disabled]), button[role="checkbox"]');
      if (first) first.focus();
    }, 120);
    return () => clearTimeout(t);
  }, [open, batchIdx]);
  useEffect(() => { loadItems(); loadVendors(); api.get("/stores").then(r => setStores(r.data.filter(s => s.status === "Active"))).catch(() => {}); }, []);

  const pickSupplier = async (vid) => {
    const v = vendors.find(x => x.id === vid);
    setForm(f => ({ ...f, party_id: vid, party_name: v?.name || "", party_phone: v?.phone || "", party_address: v?.address || "" }));
    if (vid) {
      try { const { data } = await api.get(`/vendors/${vid}/outstanding`); setSupInfo(data); }
      catch { setSupInfo(null); }
    } else setSupInfo(null);
  };

  // Mobile Number search: exact phone match in Supplier Master auto-selects the
  // supplier; no match keeps the manually typed number.
  const onMobileChange = (v) => {
    const digits = v.replace(/[^\d+]/g, "");
    const m = vendors.find(c => (c.phone || "") === digits);
    if (m) { if (m.id !== form.party_id) pickSupplier(m.id); return; }
    setForm(f => {
      const sel = vendors.find(c => c.id === f.party_id);
      return { ...f, party_phone: digits, ...(sel && (sel.phone || "") !== digits ? { party_id: "" } : {}) };
    });
  };

  // Rate Master rate-type: re-price every item line from the chosen rate; lines stay editable.
  const applyRateType = (rt) => {
    setForm(f => ({
      ...f, rate_type: rt,
      lines: f.lines.map(l => {
        if (!l.item_id) return l;
        const it = items.find(x => x.id === l.item_id);
        if (!it) return l;
        return { ...l, rate: Number(it[rt]) || Number(it.mrp) || Number(it.per_unit_cost) || 0 };
      }),
    }));
  };

  const saveSupplier = async () => {
    try {
      const r = await api.post("/vendors", sup);
      toast.success("Supplier added");
      await loadVendors();
      setAddSupOpen(false);
      setSup({ name: "", phone: "", gstin: "", payment_terms: "", address: "" });
      pickSupplier(r.data.id);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const setLineItem = (i, itemId) => {
    const it = items.find(x => x.id === itemId);
    setForm(f => {
      const lines = [...f.lines];
      lines[i] = { ...lines[i], item_id: itemId, rate: it?.per_unit_cost || 0,
        cgst: it?.cgst ?? 9, sgst: it?.sgst ?? 9, igst: 0, discount_pct: 0,
        batches: [], batches_filled: false };
      return { ...f, lines };
    });
  };
  const setLineQty = (i, v) => setForm(f => {
    const lines = [...f.lines];
    lines[i] = { ...lines[i], qty: v, batches_filled: false };
    return { ...f, lines };
  });
  const setLineField = (i, k, v) => setForm(f => {
    const lines = [...f.lines];
    const line = { ...lines[i], [k]: v };
    if (["rate", "cgst", "sgst", "igst"].includes(k)) {
      const bk = k === "rate" ? "per_unit_cost" : k;
      line.batches = line.batches.map(b => ({ ...b, [bk]: Number(v) || 0 }));
    }
    lines[i] = line;
    return { ...f, lines };
  });
  const lineAmount = (l) => {
    const gross = (Number(l.rate) || 0) * (Number(l.qty) || 0);
    const afterDisc = Math.max(0, gross - gross * (Number(l.discount_pct) || 0) / 100);
    const taxPct = form.tax_type === "GST" ? (Number(l.cgst) || 0) + (Number(l.sgst) || 0) : form.tax_type === "IGST" ? (Number(l.igst) || 0) : 0;
    if (form.bill_calc === "Inclusive" && taxPct > 0) return round2(afterDisc);
    return round2(afterDisc + afterDisc * taxPct / 100);
  };
  const addLine = () => setForm(f => ({ ...f, lines: [...f.lines, { ...emptyLine }] }));
  const delLine = (i) => setForm(f => ({ ...f, lines: f.lines.filter((_, x) => x !== i) }));

  const openBatchEntry = (i) => {
    const l = form.lines[i];
    if (!l.item_id) return toast.error("Pick an item first");
    if (!(Number(l.qty) > 0)) return toast.error("Enter quantity first");
    if (l.batches.length === 0) {
      const it = items.find(x => x.id === l.item_id);
      setForm(f => {
        const lines = [...f.lines];
        lines[i] = { ...lines[i], batches: [{ ...emptyBatch, qty: Number(l.qty) || 1, mrp: it?.mrp || 0, per_unit_cost: it?.per_unit_cost || 0, cgst: it?.cgst ?? 9, sgst: it?.sgst ?? 9 }] };
        return { ...f, lines };
      });
    }
    setBatchIdx(i);
  };

  const setBatch = (i, bi, k, v) => setForm(f => {
    const lines = [...f.lines];
    const batches = [...lines[i].batches];
    batches[bi] = { ...batches[bi], [k]: v };
    lines[i] = { ...lines[i], batches };
    return { ...f, lines };
  });
  const addBatch = (i) => setForm(f => {
    const lines = [...f.lines];
    const last = lines[i].batches[lines[i].batches.length - 1] || emptyBatch;
    lines[i] = { ...lines[i], batches: [...lines[i].batches, { ...emptyBatch, cgst: last.cgst, sgst: last.sgst, igst: last.igst, mrp: last.mrp, per_unit_cost: last.per_unit_cost, qty: 1 }] };
    return { ...f, lines };
  });
  const delBatch = (i, bi) => setForm(f => {
    const lines = [...f.lines];
    lines[i] = { ...lines[i], batches: lines[i].batches.filter((_, x) => x !== bi) };
    if (lines[i].batches.length === 0) lines[i].batches = [{ ...emptyBatch }];
    return { ...f, lines };
  });
  const setBatchSerial = (i, bi, sIdx, v) => setForm(f => {
    const lines = [...f.lines];
    const batches = [...lines[i].batches];
    const serials = [...(batches[bi].serial_numbers || [])];
    serials[sIdx] = v;
    batches[bi] = { ...batches[bi], serial_numbers: serials };
    lines[i] = { ...lines[i], batches };
    return { ...f, lines };
  });

  const validateBatches = (i) => {
    const l = form.lines[i];
    const it = items.find(x => x.id === l.item_id);
    const targetQty = Number(l.qty) || 0;
    const sumQty = l.batches.reduce((a, b) => a + (Number(b.qty) || 0), 0);
    for (const b of l.batches) {
      if (!b.batch_number || !String(b.batch_number).trim()) return `Batch number required (${it?.item_name || "item"})`;
      if (!(Number(b.qty) > 0)) return `Batch qty must be > 0 (${it?.item_name || "item"})`;
      if (it?.warranty === "Yes") {
        const serials = (b.serial_numbers || []).filter(s => s && s.trim());
        if (serials.length !== Number(b.qty)) return `Fill ${b.qty} serial number(s) for batch ${b.batch_number}`;
      }
    }
    if (sumQty !== targetQty) return `Batch quantities total ${sumQty} but item quantity is ${targetQty} — they must match`;
    return null;
  };

  const forwardFromBatch = () => {
    const err = validateBatches(batchIdx);
    if (err) return toast.error(err);
    setForm(f => {
      const lines = [...f.lines];
      const l = lines[batchIdx];
      const sq = l.batches.reduce((a, b) => a + (Number(b.qty) || 0), 0);
      const sg = l.batches.reduce((a, b) => a + (Number(b.per_unit_cost) || 0) * (Number(b.qty) || 0), 0);
      const lastB = l.batches[l.batches.length - 1] || {};
      lines[batchIdx] = { ...l, batches_filled: true,
        rate: sq ? round2(sg / sq) : 0,
        cgst: Number(lastB.cgst) || 0, sgst: Number(lastB.sgst) || 0, igst: Number(lastB.igst) || 0 };
      return { ...f, lines };
    });
    const next = batchIdx + 1;
    setBatchIdx(null);
    if (next < form.lines.length) {
      toast.success(`Batch details saved for item ${batchIdx + 1} — next item`);
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="line-item-${next}"]`);
        if (el) el.click();
      }, 60);
    } else {
      toast.success("All batch details saved — add next item or save");
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="add-line-btn"]`);
        if (el) el.focus();
      }, 60);
    }
  };

  const totals = useMemo(() => {
    let net = 0, cgst = 0, sgst = 0;
    form.lines.forEach(l => {
      if (!l.batches_filled) return;
      const gross = (Number(l.rate) || 0) * (Number(l.qty) || 0);
      const d = gross * (Number(l.discount_pct) || 0) / 100;
      const incl = Math.max(0, gross - d);
      const taxPct = form.tax_type === "GST" ? (Number(l.cgst) || 0) + (Number(l.sgst) || 0) : form.tax_type === "IGST" ? (Number(l.igst) || 0) : 0;
      const n = form.bill_calc === "Inclusive" && taxPct > 0 ? round2(incl / (1 + taxPct / 100)) : incl;
      let c = 0, s = 0;
      if (form.tax_type === "GST") { c = n * (Number(l.cgst) || 0) / 100; s = n * (Number(l.sgst) || 0) / 100; }
      else if (form.tax_type === "IGST") { c = n * (Number(l.igst) || 0) / 100; }
      net += n; cgst += c; sgst += s;
    });
    const pre = net + cgst + sgst;
    const od = Number(form.overall_discount) || 0;
    const effDisc = form.overall_discount_type === "pct" ? round2(pre * od / 100) : od;
    return { net: round2(net), cgst: round2(cgst), sgst: round2(sgst), effDisc, total: round2(pre - effDisc) };
  }, [form.lines, form.tax_type, form.bill_calc, form.overall_discount, form.overall_discount_type]);

  const openNew = () => { setEditId(null); setSupInfo(null); setBatchIdx(null); setForm(emptyForm()); setOpen(true); };

  // Restore minimised purchase form state
  useEffect(() => {
    const p = consumeRestore("txn-purchase");
    if (!p || p.type !== type) return;
    setEditId(p.editId || null);
    setForm(p.form);
    setOpen(true);
  }, [pending, type]); // eslint-disable-line

  const refOfPurchase = () => {
    if (editId) return form.invoice_number || "Edit";
    return form.party_name || (vendors.find(v => v.id === form.party_id)?.name) || "New";
  };
  const isDirtyPurchase = () => {
    const l0 = form.lines?.[0];
    return !!(form.party_id || form.party_name || form.invoice_number || form.notes || (l0 && l0.item_id));
  };
  const minimisePurchase = () => {
    minimize({ kind: "txn-purchase", label: `${editId ? "Edit" : "New"} ${label || "Purchase"}`,
               ref: refOfPurchase(), state: { type, form, editId },
               path: `/transactions?type=${type}`, dirty: isDirtyPurchase() });
    setOpen(false); setBatchIdx(null);
    toast.success("Minimised to taskbar");
  };
  // Deep-link edit: ?edit=<id> opens the form pre-filled (used by Transaction Master)
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const eid = sp.get("edit");
    if (!eid) return;
    (async () => {
      try { const res = await api.get(`/transactions/${eid}`); openEdit(res.data); }
      catch (e) { toast.error(formatErr(e)); }
      sp.delete("edit");
      window.history.replaceState(null, "", `${window.location.pathname}?${sp.toString()}`);
    })();
  }, []); // eslint-disable-line
  const openEdit = (t) => {
    setEditId(t.id); setBatchIdx(null);
    const lines = (t.lines || []).map(l => ({
      item_id: l.item_id,
      qty: l.qty,
      rate: l.rate || 0,
      discount_pct: l.discount_pct || 0,
      cgst: l.cgst ?? 0,
      sgst: l.sgst ?? 0,
      igst: t.tax_type === "IGST" ? (l.cgst || 0) : 0,
      batches: (l.batches && l.batches.length ? l.batches : [{ ...emptyBatch, qty: l.qty, mrp: l.rate, per_unit_cost: l.per_unit_cost || l.rate, cgst: l.cgst, sgst: l.sgst, serial_numbers: l.serial_number ? [l.serial_number] : [] }])
        .map(b => ({ ...emptyBatch, ...b, expiry_date: b.expiry_date || "", serial_numbers: b.serial_numbers || [] })),
      batches_filled: true,
    }));
    setForm({ party_id: t.party_id || "", party_name: t.party_name || "", party_phone: t.party_phone || "",
      txn_date: t.txn_date || todayISO(), rate_type: t.rate_type || "purchase_rate",
      party_address: t.party_address || "", bill_calc: t.bill_calc || "Exclusive",
      invoice_number: t.invoice_number || "", tax_type: t.tax_type || "GST",
      store_id: t.store_id || "", lines,
      overall_discount: t.overall_discount || 0, overall_discount_type: t.overall_discount_type || "amt", notes: t.notes || "" });
    if (t.party_id) pickSupplier(t.party_id);
    setOpen(true);
  };

  const allBatchesFilled = form.lines.every(l => l.item_id && Number(l.qty) > 0 && l.batches_filled);

  const save = () => {
    if (!form.party_id) return toast.error("Supplier is required");
    if (form.lines.some(l => !l.item_id)) return toast.error("Pick items in every line");
    for (let i = 0; i < form.lines.length; i++) {
      if (!form.lines[i].batches_filled) return toast.error(`Enter batch details for item line ${i + 1} before saving`);
      const err = validateBatches(i);
      if (err) return toast.error(`Line ${i + 1}: ${err}`);
    }
    if (appSettings?.auto_payment_panel === false) confirmSave([]);
    else setPayOpen(true);
  };

  const confirmSave = async (payRows) => {
    setSaving(true);
    try {
      const payload = {
        type, party_type: "vendor", party_id: form.party_id,
        party_name: form.party_name || null, party_phone: form.party_phone || null,
        invoice_number: form.invoice_number || null, tax_type: form.tax_type,
        txn_date: form.txn_date || null, rate_type: form.rate_type || null,
        party_address: form.party_address || null, bill_calc: form.bill_calc || "Exclusive",
        store_id: form.store_id || null, notes: form.notes || null,
        overall_discount: Number(form.overall_discount) || 0, overall_discount_type: form.overall_discount_type,
        lines: form.lines.map(l => ({
          item_id: l.item_id, qty: Number(l.qty), rate: Number(l.rate) || 0, discount_pct: Number(l.discount_pct) || 0,
          batches: l.batches.map(b => ({
            batch_number: String(b.batch_number).trim(),
            qty: Number(b.qty), mrp: Number(b.mrp) || 0,
            per_unit_cost: Number(b.per_unit_cost) || 0,
            cgst: Number(b.cgst) || 0, sgst: Number(b.sgst) || 0, igst: Number(b.igst) || 0,
            expiry_date: b.expiry_date || null,
            serial_numbers: (b.serial_numbers || []).map(s => String(s).trim()).filter(Boolean),
          })),
        })),
      };
      let tid = editId, saved = null;
      if (editId) { const r = await api.patch(`/transactions/${editId}`, payload); saved = r.data; }
      else { const r = await api.post("/transactions", payload); saved = r.data; tid = r.data.id; }
      for (const p of payRows) await api.post(`/transactions/${tid}/payment`, { amount: Number(p.amount), method: p.mode });
      toast.success(editId ? "Purchase updated" : "Purchase saved");
      setPayOpen(false); setOpen(false); setBatchIdx(null); load(); loadItems();
      if (tid) { setSavedDoc({ id: tid, number: saved?.invoice_number || saved?.txn_number || "" }); if (appSettings?.auto_print_after_save) openTxnPdf(tid); }
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!confirm("Delete this purchase? Stock, batches and payments will be reversed.")) return;
    try { await api.delete(`/transactions/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const openPayment = async (t) => {
    setPayDlg(t);
    setPayForm({ amount: t.outstanding || 0, method: "cash", note: "", paid_on: todayISO() });
    try { const { data } = await api.get(`/transactions/${t.id}/payments`); setPayments(data); }
    catch { setPayments([]); }
  };
  const savePayment = async () => {
    try {
      await api.post(`/transactions/${payDlg.id}/payment`, { ...payForm, amount: Number(payForm.amount) });
      toast.success("Payment recorded"); setPayDlg(null); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const willUpdateMaster = (it, b) => it && (
    (Number(b.mrp) && Number(b.mrp) !== Number(it.mrp)) ||
    (Number(b.per_unit_cost) && Number(b.per_unit_cost) !== Number(it.per_unit_cost || 0)) ||
    (form.tax_type === "GST" && ((Number(b.cgst) !== Number(it.cgst)) || (Number(b.sgst) !== Number(it.sgst))))
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.altKey && (e.key === "4" || e.code === "Digit4")) {
        e.preventDefault();
        const lineEl = e.target && e.target.closest ? e.target.closest('[data-testid^="line-"]') : null;
        const lm = lineEl ? (lineEl.getAttribute("data-testid") || "").match(/(\d+)$/) : null;
        if (!lm) return;
        const iid = formRef.current.lines[Number(lm[1])]?.item_id;
        if (!iid) { toast.error("Pick an item in this line first"); return; }
        setRegItemId(iid);
        return;
      }
      if (regItemId) { if (e.key === "Escape") { e.preventDefault(); setRegItemId(null); } return; }
      if (payOpen) {
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") { e.preventDefault(); document.querySelector('[data-testid="pay-save-btn"]:not([disabled])')?.click(); }
        return;
      }
      if (e.key === "Escape") { e.preventDefault(); setOpen(false); setBatchIdx(null); }
      else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        if (batchIdx !== null) forwardFromBatch();
        else document.querySelector('[data-testid="save-purchase-btn"]:not([disabled])')?.click();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, batchIdx, payOpen, regItemId]); // eslint-disable-line

  const batchLine = batchIdx !== null ? form.lines[batchIdx] : null;
  const batchItem = batchLine ? items.find(x => x.id === batchLine.item_id) : null;
  const batchSumQty = batchLine ? batchLine.batches.reduce((a, b) => a + (Number(b.qty) || 0), 0) : 0;

  const handleEnterNav = createKeyboardNav({
    selector: 'input:not([disabled]), [role="combobox"], [data-line-amt], button[data-testid^="enter-batch-"]:not([disabled]), button[data-testid="add-line-btn"], [data-net-amount]',
    endTarget: true,
    skipInside: '[data-testid="batch-entry-page"], [data-testid="payment-panel"], [data-testid="item-register-overlay"]',
    addLineBtn: true,
  });
  const handleBatchEnterNav = (e) => {
    if (e.key === "Backspace") { backspacePrev(e, e.currentTarget, 'input:not([disabled])'); return; }
    if (e.key.startsWith("Arrow")) { arrowNav(e, e.currentTarget, 'input:not([disabled])'); return; }
    if (e.key !== "Enter") return;
    const t = e.target;
    if (t.tagName === "TEXTAREA") return;
    const container = e.currentTarget;
    const tid = t.getAttribute("data-testid") || "";
    if (tid.startsWith("add-batch-")) {
      // Add Another Batch is manual-click only — Enter must not add rows
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    // plain buttons (back / forward) keep native Enter = click
    if (t.tagName === "BUTTON") return;
    e.preventDefault();
    e.stopPropagation();
    const fields = [...container.querySelectorAll('input:not([disabled])')]
      .filter(el => el.offsetParent !== null);
    const idx = fields.indexOf(t);
    if (idx === -1) return;
    const next = fields[idx + 1];
    if (next) { next.focus(); if (next.select) { try { next.select(); } catch {} } }
    else forwardFromBatch();
  };

  return (
    <div>
      {!open && (<>
      <div className="flex flex-wrap items-end gap-3 mb-4 border border-border bg-card rounded-sm p-3" data-testid="purchase-filter-bar" onKeyDownCapture={handleSimpleFormNav}>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">From</Label>
          <Input data-testid="date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="mt-1 rounded-sm bg-secondary border-border h-9 w-40"/>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">To</Label>
          <Input data-testid="date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="mt-1 rounded-sm bg-secondary border-border h-9 w-40"/>
        </div>
        <Button data-testid="today-btn" size="sm" variant="outline" onClick={() => { setDateFrom(todayISO()); setDateTo(todayISO()); }} className="rounded-sm text-xs uppercase tracking-wider">Today</Button>
        <Button data-testid="all-dates-btn" size="sm" variant="outline" onClick={() => { setDateFrom(""); setDateTo(""); }} className="rounded-sm text-xs uppercase tracking-wider">All Dates</Button>
        <div className="flex-1 min-w-[220px]">
          <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Search (Invoice #, Supplier)</Label>
          <Input data-testid="purchase-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Type to search…" className="mt-1 rounded-sm bg-secondary border-border h-9"/>
        </div>
        <div className="ml-auto flex items-end gap-2">
          <span className="text-xs text-muted-foreground pb-2" data-testid="purchase-count">{list.length} record(s)</span>
          {canModify && (
            <Button data-testid="new-purchase-btn" onClick={() => isRetail ? navigate("/reorder-purchase") : openNew()} className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] text-xs">
              <Plus className="w-4 h-4 mr-1"/> New {label}
            </Button>
          )}
        </div>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-hidden overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th className="text-left p-3">Invoice #</th>
              <th className="text-left p-3">Date</th>
              <th className="text-left p-3">Supplier</th>
              <th className="text-left p-3">Tax</th>
              <th className="text-right p-3">Items</th>
              <th className="text-right p-3">Total</th>
              <th className="text-right p-3">Paid</th>
              <th className="text-right p-3">Outstanding</th>
              <th className="text-left p-3">Payment Status</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={10} className="p-6 text-center text-muted-foreground">No {label.toLowerCase()} records</td></tr>}
            {pg.pageItems.map(t => (
              <tr key={t.id} data-testid={`purchase-row-${t.id}`} className="border-t border-border hover:bg-secondary/50">
                <td className="p-3 font-mono text-xs text-primary">{t.invoice_number || t.txn_number}</td>
                <td className="p-3 font-mono text-xs text-muted-foreground">{new Date(t.created_at).toLocaleDateString()}</td>
                <td className="p-3 text-xs">{t.party_name || "-"}</td>
                <td className="p-3 text-[10px] uppercase tracking-wider text-muted-foreground">{t.tax_type || "GST"}</td>
                <td className="p-3 text-right font-mono text-xs">{t.lines.length}</td>
                <td className="p-3 text-right font-mono">{inr(t.total)}</td>
                <td className="p-3 text-right font-mono text-xs text-green-500">{inr(t.paid_amount || 0)}</td>
                <td className="p-3 text-right font-mono text-xs">{inr(t.outstanding || 0)}</td>
                <td className="p-3"><span data-testid={`pay-status-${t.id}`} className={`px-2 py-1 text-[10px] uppercase tracking-wider rounded-sm border ${statusColor[t.payment_status] || statusColor.Unpaid}`}>{t.payment_status}</span></td>
                <td className="p-3 text-right space-x-1 whitespace-nowrap">
                  {canModify && t.payment_status !== "Paid" && (
                    <Button data-testid={`record-pay-${t.id}`} size="sm" variant="ghost" onClick={() => openPayment(t)} className="h-7 text-xs"><Wallet className="w-3 h-3 mr-1"/>Pay</Button>
                  )}
                  {canModify && <Button data-testid={`edit-purchase-${t.id}`} size="sm" variant="ghost" onClick={() => openEdit(t)} className="h-7 w-7 p-0"><Pencil className="w-3 h-3"/></Button>}
                  {canModify && <Button data-testid={`del-purchase-${t.id}`} size="sm" variant="ghost" onClick={() => del(t.id)} className="h-7 w-7 p-0 text-destructive"><Trash2 className="w-3 h-3"/></Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <PaginationBar pg={pg}/>
      </div>

      {isRetail && canModify && <ReorderRecords className="mt-4" />}
      </>)}
      {open && (
      <div className="border border-border bg-card rounded-sm p-6 min-h-[70vh]" data-testid="purchase-form-page" onKeyDownCapture={handleEnterNav}>

          {batchIdx === null ? (
            <>
              <div className="flex items-center justify-between border-b border-border pb-3">
                <div className="flex items-center gap-3">
                <Button type="button" data-testid="form-back-btn" variant="outline" onClick={() => { setOpen(false); setBatchIdx(null); }} className="rounded-sm text-xs uppercase tracking-wider"><ArrowLeft className="w-3 h-3 mr-1"/>Back to List</Button>
                  <h2 className="font-display uppercase text-lg">{editId ? "Edit" : "New"} {label}</h2>
                </div>
                <div className="flex items-center gap-2">
                  <Button type="button" data-testid="minimise-purchase-btn" variant="outline" onClick={minimisePurchase} className="rounded-sm text-xs uppercase tracking-wider"><Minimize2 className="w-3 h-3 mr-1"/>Minimise</Button>
                  <Button type="button" data-testid="form-cancel-btn" variant="ghost" onClick={() => { setOpen(false); setBatchIdx(null); }} className="rounded-sm text-xs uppercase tracking-wider text-muted-foreground">Cancel</Button>
                </div>
              </div>
              <div className="space-y-3">
                <div className="grid grid-cols-[minmax(0,2.2fr)_repeat(3,minmax(0,1fr))] gap-3">
                  {/* Line 1: Supplier (wide), Mobile Number, Date, Invoice Number (compact) */}
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Supplier *</Label>
                    <div className="flex gap-2 mt-1">
                      <Select value={form.party_id} onValueChange={pickSupplier}>
                        <SelectTrigger data-testid="purchase-supplier" className="rounded-sm bg-secondary border-border flex-1 min-w-0"><SelectValue placeholder="Select supplier"/></SelectTrigger>
                        <SelectContent>{vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name} · {v.phone}</SelectItem>)}</SelectContent>
                      </Select>
                      <Button type="button" data-testid="add-supplier-btn" variant="outline" size="icon" onClick={() => setAddSupOpen(true)} title="New Supplier" className="rounded-sm h-9 w-9 shrink-0"><Plus className="w-4 h-4"/></Button>
                    </div>
                    {supInfo && (
                      <div data-testid="supplier-info" className="mt-2 text-[11px] font-mono border border-border bg-secondary/40 rounded-sm p-2 space-y-0.5">
                        {supInfo.gstin && <div><span className="text-muted-foreground">GSTIN:</span> {supInfo.gstin}</div>}
                        {supInfo.payment_terms && <div><span className="text-muted-foreground">Terms:</span> {supInfo.payment_terms}</div>}
                        <div><span className="text-muted-foreground">Outstanding:</span> <span className="text-primary">{inr(supInfo.outstanding)}</span></div>
                      </div>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Mobile Number</Label>
                    <Input data-testid="purchase-mobile" value={form.party_phone} onChange={(e) => onMobileChange(e.target.value)} placeholder="Search by mobile…" className="mt-1 rounded-sm bg-secondary border-border font-mono"/>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Date</Label>
                    <Input type="date" data-testid="purchase-date" value={form.txn_date} onChange={(e) => setForm(f => ({ ...f, txn_date: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Invoice Number</Label>
                    <Input data-testid="purchase-invoice-number" value={form.invoice_number} onChange={(e) => setForm(f => ({ ...f, invoice_number: e.target.value }))} placeholder="Supplier invoice ref" className="mt-1 rounded-sm bg-secondary border-border font-mono"/>
                  </div>
                </div>
                <div className="grid grid-cols-[minmax(0,1.2fr)_repeat(4,minmax(0,1fr))] gap-3">
                  {/* Line 2: Address+Rate align under Supplier; Tax/Store/Bill Calc under Mobile/Date/Invoice */}
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Address</Label>
                    <Input data-testid="purchase-address" value={form.party_address} onChange={(e) => setForm(f => ({ ...f, party_address: e.target.value }))} placeholder="Auto-filled from supplier" className="mt-1 rounded-sm bg-secondary border-border"/>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Rate (Rate Master)</Label>
                    <Select value={form.rate_type} onValueChange={applyRateType}>
                      <SelectTrigger data-testid="purchase-rate-type" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mrp">MRP</SelectItem>
                        <SelectItem value="wholesale_rate">Wholesale Rate</SelectItem>
                        <SelectItem value="purchase_rate">Purchase Rate</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Tax Type *</Label>
                    <Select value={form.tax_type} onValueChange={(v) => setForm(f => ({ ...f, tax_type: v }))}>
                      <SelectTrigger data-testid="purchase-tax-type" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="GST">GST (CGST + SGST)</SelectItem>
                        <SelectItem value="IGST">IGST</SelectItem>
                        <SelectItem value="Exempt">Exempt</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Store</Label>
                    <Select value={form.store_id} onValueChange={(v) => setForm(f => ({ ...f, store_id: v }))}>
                      <SelectTrigger data-testid="purchase-store" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue placeholder="Select store (optional)"/></SelectTrigger>
                      <SelectContent>{stores.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Bill Calculation</Label>
                    <Select value={form.bill_calc} onValueChange={(v) => setForm(f => ({ ...f, bill_calc: v }))}>
                      <SelectTrigger data-testid="purchase-bill-calc" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Exclusive">Exclusive of Tax</SelectItem>
                        <SelectItem value="Inclusive">Inclusive of Tax</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Item lines */}
                <div className="space-y-2">
                  <div className="grid grid-cols-[minmax(150px,1.6fr)_56px_76px_72px_76px_64px_60px_60px_88px_140px_36px] gap-2 items-end px-[9px] text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                    <div className="text-center">Item *</div>
                    <div className="text-center">Quantity *</div>
                    <div className="text-center">Part No</div>
                    <div className="text-center">HSN</div>
                    <div className="text-center">Rate *</div>
                    <div className="text-center">Discount %</div>
                    {form.tax_type === "GST" && <><div className="text-center">CGST %</div><div className="text-center">SGST %</div></>}
                    {form.tax_type === "IGST" && <div className="text-center col-span-2">IGST %</div>}
                    {form.tax_type === "Exempt" && <div className="col-span-2 text-center">Tax Exempt</div>}
                    <div className="text-center">Amount</div>
                    <div></div><div></div>
                  </div>
                  {form.lines.map((l, i) => {
                    const it = items.find(x => x.id === l.item_id);
                    const sumQty = l.batches.reduce((a, b) => a + (Number(b.qty) || 0), 0);
                    return (
                      <div key={i} data-testid={`line-${i}`} data-kb-row={i} className={`border rounded-sm p-2 ${l.batches_filled ? "border-green-500/40 bg-green-500/5" : "border-border bg-secondary/20"}`}>
                        <div className="grid grid-cols-[minmax(150px,1.6fr)_56px_76px_72px_76px_64px_60px_60px_88px_140px_36px] gap-2 items-center">
                          <ItemCombobox
                            items={items} value={l.item_id} testId={`line-item-${i}`}
                            labelFn={(x) => `${x.item_code ? x.item_code + " · " : ""}${x.item_name} · Cost ₹${x.per_unit_cost || x.mrp}${x.warranty === "Yes" ? " · Warranty" : ""}`}
                            onSelect={(x) => setLineItem(i, x.id)}
                            afterSelect={() => setTimeout(() => document.querySelector(`[data-testid="line-qty-${i}"]`)?.focus(), 50)}
                          />
                          <Input data-testid={`line-qty-${i}`} type="number" min="1" value={l.qty} onChange={(e) => setLineQty(i, e.target.value)} className="rounded-sm bg-secondary border-border h-9 text-xs text-right"/>
                          <Input data-testid={`line-partno-${i}`} value={it?.part_number || ""} readOnly className="rounded-sm bg-secondary border-border h-9 text-xs font-mono text-muted-foreground text-center"/>
                          <Input data-testid={`line-hsn-${i}`} value={it?.hsn_code || ""} readOnly className="rounded-sm bg-secondary border-border h-9 text-xs font-mono text-muted-foreground text-center"/>
                          <Input data-testid={`line-rate-${i}`} type="number" value={l.rate} onChange={(e) => setLineField(i, "rate", e.target.value)} className="rounded-sm bg-secondary border-border h-9 text-xs text-right"/>
                          <Input data-testid={`line-disc-${i}`} type="number" min="0" max="100" value={l.discount_pct} onChange={(e) => setLineField(i, "discount_pct", e.target.value)} className="rounded-sm bg-secondary border-border h-9 text-xs text-right"/>
                          {form.tax_type === "GST" && <>
                            <Input data-testid={`line-cgst-${i}`} type="number" value={l.cgst} onChange={(e) => setLineField(i, "cgst", e.target.value)} className="rounded-sm bg-secondary border-border h-9 text-xs text-right"/>
                            <Input data-testid={`line-sgst-${i}`} type="number" value={l.sgst} onChange={(e) => setLineField(i, "sgst", e.target.value)} className="rounded-sm bg-secondary border-border h-9 text-xs text-right"/>
                          </>}
                          {form.tax_type === "IGST" && (
                            <Input data-testid={`line-igst-${i}`} type="number" value={l.igst} onChange={(e) => setLineField(i, "igst", e.target.value)} className="rounded-sm bg-secondary border-border h-9 text-xs text-right col-span-2"/>
                          )}
                          {form.tax_type === "Exempt" && <div className="col-span-2 text-right text-[10px] uppercase text-muted-foreground">—</div>}
                          <div data-testid={`line-amount-${i}`} data-line-amt tabIndex={-1} className="text-right font-mono text-xs text-primary rounded-sm px-1 focus:outline-none focus:ring-1 focus:ring-primary">{inr(lineAmount(l))}</div>
                          <Button type="button" data-testid={`enter-batch-${i}`} onClick={() => openBatchEntry(i)}
                            disabled={!l.item_id || !(Number(l.qty) > 0)}
                            className={`rounded-sm text-[10px] uppercase tracking-wider h-9 whitespace-nowrap ${l.batches_filled ? "bg-green-600 hover:bg-green-600 text-white" : "bg-primary text-primary-foreground"}`}>
                            <Layers className="w-3 h-3 mr-1"/>{l.batches_filled ? "Edit Batch" : "Enter Batch Details"}
                          </Button>
                          <Button type="button" size="sm" variant="ghost" onClick={() => delLine(i)} disabled={form.lines.length === 1} className="h-9 w-9 p-0 text-destructive"><Trash2 className="w-4 h-4"/></Button>
                        </div>
                        {l.batches_filled && (
                          <div data-testid={`line-summary-${i}`} className="mt-2 text-xs font-mono text-green-500 flex items-center gap-3">
                            <span>{l.batches.length} batch(es) · {sumQty} unit(s)</span>
                            <span className="text-muted-foreground">{l.batches.map(b => b.batch_number).join(", ")}</span>
                            {it?.warranty === "Yes" && <span className="text-primary">{l.batches.reduce((a, b) => a + (b.serial_numbers || []).filter(Boolean).length, 0)} serial(s) captured</span>}
                          </div>
                        )}
                        {it && !l.batches_filled && it.warranty === "Yes" && (
                          <div className="mt-1 text-[10px] uppercase tracking-wider text-primary">Warranty item — serial numbers required in batch entry</div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <Button type="button" data-testid="add-line-btn" size="sm" variant="outline" onClick={addLine} className="rounded-sm text-xs uppercase tracking-wider"><Plus className="w-3 h-3 mr-1"/>Add Item Line</Button>

                <div className="grid grid-cols-2 gap-4 border-t border-border pt-3">
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Notes</Label>
                    <Textarea data-testid="purchase-notes" value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" rows={2}/>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-muted-foreground">Net Amount</span><span className="font-mono" data-testid="totals-net">{inr(totals.net)}</span></div>
                    {form.tax_type === "GST" && <>
                      <div className="flex justify-between text-muted-foreground"><span>CGST</span><span className="font-mono" data-testid="totals-cgst">{inr(totals.cgst)}</span></div>
                      <div className="flex justify-between text-muted-foreground"><span>SGST</span><span className="font-mono" data-testid="totals-sgst">{inr(totals.sgst)}</span></div>
                    </>}
                    {form.tax_type === "IGST" && <div className="flex justify-between text-muted-foreground"><span>IGST</span><span className="font-mono" data-testid="totals-igst">{inr(totals.cgst)}</span></div>}
                    <div className="flex justify-between items-center">
                      <span className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Overall Discount</span>
                      <div className="flex items-center gap-1">
                        {form.overall_discount_type === "pct" && Number(form.overall_discount) > 0 && <span data-testid="overall-discount-amt" className="text-[10px] font-mono text-muted-foreground whitespace-nowrap">= {inr(totals.effDisc)}</span>}
                        <Input data-testid="overall-discount" data-end-target type="number" min="0" {...(form.overall_discount_type === "pct" ? { max: "100" } : {})} value={form.overall_discount} onChange={(e) => setForm(f => ({ ...f, overall_discount: e.target.value }))} className="w-24 h-8 rounded-sm bg-secondary border-border text-right"/>
                        <div className="flex border border-border rounded-sm overflow-hidden shrink-0">
                          <button type="button" data-testid="disc-type-amt" onClick={() => setForm(f => ({ ...f, overall_discount_type: "amt" }))} className={`px-2 h-8 text-xs ${form.overall_discount_type === "amt" ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>₹</button>
                          <button type="button" data-testid="disc-type-pct" onClick={() => setForm(f => ({ ...f, overall_discount_type: "pct" }))} className={`px-2 h-8 text-xs ${form.overall_discount_type === "pct" ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>%</button>
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-between border-t border-border pt-2 items-center">
                      <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Grand Total</span>
                      <span data-testid="totals-grand" data-net-amount tabIndex={-1} className="font-display font-black text-2xl text-primary rounded-sm px-1 focus:outline-none focus:ring-2 focus:ring-primary">{inr(totals.total)}</span>
                    </div>
                  </div>
                </div>

                {!allBatchesFilled && (
                  <div data-testid="save-blocked-hint" className="text-[11px] uppercase tracking-wider text-yellow-500 border border-yellow-500/30 bg-yellow-500/5 rounded-sm p-2">
                    Enter batch details for all item lines before saving
                  </div>
                )}
                <Button data-testid="save-purchase-btn" onClick={save} disabled={!allBatchesFilled} className="w-full rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em]">{editId ? "Update Purchase" : `Save ${label}`}</Button>
              </div>
            </>
          ) : (
            /* -------- Batch Entry Page (full-page view for current item) -------- */
            <div data-testid="batch-entry-page" onKeyDownCapture={handleBatchEnterNav}>
              <div className="flex items-center gap-3 border-b border-border pb-3">
                <Button type="button" data-testid="batch-back" size="sm" variant="ghost" onClick={() => setBatchIdx(null)} className="h-8 w-8 p-0"><ArrowLeft className="w-4 h-4"/></Button>
                <h2 className="font-display uppercase text-lg">Batch Details — {batchItem?.item_name}</h2>
                <span className="text-muted-foreground text-xs font-normal">Line {batchIdx + 1} of {form.lines.length}</span>
              </div>
              <div className="space-y-3 mt-2">
                <div className="flex items-center gap-4 text-xs font-mono border border-border bg-secondary/40 rounded-sm p-2">
                  <span className="text-muted-foreground">Target Qty: <span className="text-foreground">{batchLine?.qty}</span></span>
                  <span className="text-muted-foreground">Entered: <span className={batchSumQty === Number(batchLine?.qty) ? "text-green-500" : "text-yellow-500"}>{batchSumQty}</span></span>
                  <span className="text-muted-foreground">Tax: <span className="text-foreground">{form.tax_type}</span></span>
                  {batchItem?.warranty === "Yes" && <span className="text-primary uppercase tracking-wider text-[10px]">Warranty item — serial per unit required</span>}
                </div>

                {batchLine.batches.map((b, bi) => (
                  <div key={bi} data-testid={`batch-${batchIdx}-${bi}`} data-kb-row={bi} className="border border-border rounded-sm p-3 bg-secondary/20 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Batch {bi + 1}</span>
                      <Button type="button" size="sm" variant="ghost" onClick={() => delBatch(batchIdx, bi)} disabled={batchLine.batches.length === 1} className="h-7 w-7 p-0 text-destructive"><Trash2 className="w-3 h-3"/></Button>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Batch Number *</Label>
                        <Input data-testid={`batch-number-${batchIdx}-${bi}`} value={b.batch_number} onChange={(e) => setBatch(batchIdx, bi, "batch_number", e.target.value)} className="mt-1 rounded-sm bg-secondary border-border font-mono"/>
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Quantity *</Label>
                        <Input data-testid={`batch-qty-${batchIdx}-${bi}`} type="number" min="1" value={b.qty} onChange={(e) => setBatch(batchIdx, bi, "qty", e.target.value)} className="mt-1 rounded-sm bg-secondary border-border text-right"/>
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Per Unit Cost *</Label>
                        <Input data-testid={`batch-cost-${batchIdx}-${bi}`} type="number" value={b.per_unit_cost} onChange={(e) => setBatch(batchIdx, bi, "per_unit_cost", e.target.value)} className="mt-1 rounded-sm bg-secondary border-border text-right"/>
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">MRP *</Label>
                        <Input data-testid={`batch-mrp-${batchIdx}-${bi}`} type="number" value={b.mrp} onChange={(e) => setBatch(batchIdx, bi, "mrp", e.target.value)} className="mt-1 rounded-sm bg-secondary border-border text-right"/>
                      </div>
                      {form.tax_type === "GST" && <>
                        <div>
                          <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">CGST %</Label>
                          <Input data-testid={`batch-cgst-${batchIdx}-${bi}`} type="number" value={b.cgst} onChange={(e) => setBatch(batchIdx, bi, "cgst", e.target.value)} className="mt-1 rounded-sm bg-secondary border-border text-right"/>
                        </div>
                        <div>
                          <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">SGST %</Label>
                          <Input data-testid={`batch-sgst-${batchIdx}-${bi}`} type="number" value={b.sgst} onChange={(e) => setBatch(batchIdx, bi, "sgst", e.target.value)} className="mt-1 rounded-sm bg-secondary border-border text-right"/>
                        </div>
                      </>}
                      {form.tax_type === "IGST" && (
                        <div className="col-span-2">
                          <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">IGST %</Label>
                          <Input data-testid={`batch-igst-${batchIdx}-${bi}`} type="number" value={b.igst} onChange={(e) => setBatch(batchIdx, bi, "igst", e.target.value)} className="mt-1 rounded-sm bg-secondary border-border text-right"/>
                        </div>
                      )}
                      {form.tax_type === "Exempt" && <div className="col-span-2 text-[10px] uppercase text-muted-foreground self-end pb-2">Tax exempt — no GST applied</div>}
                      <div>
                        <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Expiry Date (optional)</Label>
                        <Input data-testid={`batch-expiry-${batchIdx}-${bi}`} type="date" value={b.expiry_date || ""} onChange={(e) => setBatch(batchIdx, bi, "expiry_date", e.target.value)} className="mt-1 rounded-sm bg-secondary border-border"/>
                      </div>
                    </div>
                    {willUpdateMaster(batchItem, b) && (
                      <div data-testid={`master-update-badge-${batchIdx}-${bi}`} className="text-[10px] uppercase tracking-wider text-yellow-500">MRP / Cost / GST differ from Item Master — will update master on save</div>
                    )}
                    {batchItem?.warranty === "Yes" && (
                      <div className="border-t border-border pt-2 space-y-2">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Serial Numbers ({Number(b.qty) || 0} unit(s)) — all required</div>
                        <div className="grid grid-cols-3 gap-2">
                          {Array.from({ length: Math.max(1, Number(b.qty) || 0) }).map((_, sIdx) => (
                            <Input key={sIdx} data-testid={`batch-serial-${batchIdx}-${bi}-${sIdx}`} placeholder={`Serial #${sIdx + 1} *`} value={(b.serial_numbers || [])[sIdx] || ""} onChange={(e) => setBatchSerial(batchIdx, bi, sIdx, e.target.value)} className="rounded-sm bg-secondary border-border h-8 text-xs font-mono"/>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                <Button type="button" data-testid={`add-batch-${batchIdx}`} size="sm" variant="outline" onClick={() => addBatch(batchIdx)} className="rounded-sm text-xs uppercase tracking-wider"><Plus className="w-3 h-3 mr-1"/>Add Another Batch</Button>

                <div className="border-t border-border pt-3 flex items-center justify-between gap-3">
                  <span className="text-[11px] text-muted-foreground uppercase tracking-wider">Press Enter or Forward to save this item's batch details</span>
                  <Button data-testid="batch-forward" onClick={forwardFromBatch} className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] text-xs">
                    {batchIdx + 1 < form.lines.length ? (<>Forward — Next Item <ArrowRight className="w-3 h-3 ml-1"/></>) : (<>Enter — Done <CornerDownLeft className="w-3 h-3 ml-1"/></>)}
                  </Button>
                </div>
              </div>
            </div>
          )}
      </div>
      )}

      {regItemId && (
        <div className="fixed inset-0 z-50 bg-background overflow-y-auto p-6" data-testid="item-register-overlay">
          <ItemRegisterView itemId={regItemId} onClose={() => setRegItemId(null)} />
        </div>
      )}

      <PaymentPanel open={payOpen} title={label} grandTotal={totals.total} saving={saving}
        onCancel={() => { setPayOpen(false); setTimeout(() => document.querySelector('[data-testid="save-purchase-btn"]')?.focus(), 60); }}
        onConfirm={confirmSave} />

      <Dialog open={!!savedDoc} onOpenChange={(v) => { if (!v) setSavedDoc(null); }}>
        <DialogContent className="rounded-sm bg-card border-border max-w-sm text-center" onKeyDownCapture={handleSimpleFormNav}>
          <DialogHeader><DialogTitle className="font-display uppercase text-center">Save Confirmation</DialogTitle></DialogHeader>
          {savedDoc && (
            <div className="space-y-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Invoice Number</div>
                <div className="font-mono font-bold text-xl text-primary mt-1" data-testid="saved-invoice-number">{savedDoc.number}</div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button data-testid="print-invoice-btn" onClick={() => openTxnPdf(savedDoc.id)} className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] text-xs"><Printer className="w-3 h-3 mr-1"/>Print Invoice</Button>
                <Button data-testid="new-transaction-btn" variant="outline" onClick={() => { setSavedDoc(null); openNew(); }} className="rounded-sm uppercase tracking-[0.15em] text-xs"><Plus className="w-3 h-3 mr-1"/>New Transaction</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Supplier dialog */}
      <Dialog open={addSupOpen} onOpenChange={setAddSupOpen}>
        <DialogContent className="rounded-sm bg-card border-border max-w-md">
          <DialogHeader><DialogTitle className="font-display uppercase">New Supplier</DialogTitle></DialogHeader>
          <div className="space-y-3" onKeyDownCapture={handleSimpleFormNav}>
            <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Name *</Label><Input data-testid="sup-name" value={sup.name} onChange={(e) => setSup(s => ({ ...s, name: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/></div>
            <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Phone *</Label><Input data-testid="sup-phone" value={sup.phone} onChange={(e) => setSup(s => ({ ...s, phone: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/></div>
            <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">GSTIN</Label><Input data-testid="sup-gstin" value={sup.gstin} onChange={(e) => setSup(s => ({ ...s, gstin: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border font-mono"/></div>
            <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Payment Terms</Label><Input data-testid="sup-terms" value={sup.payment_terms} onChange={(e) => setSup(s => ({ ...s, payment_terms: e.target.value }))} placeholder="e.g. Net 30" className="mt-1 rounded-sm bg-secondary border-border"/></div>
            <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Address</Label><Input data-testid="sup-address" value={sup.address} onChange={(e) => setSup(s => ({ ...s, address: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/></div>
            <Button data-testid="save-supplier-btn" onClick={saveSupplier} disabled={!sup.name || !sup.phone} className="w-full rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em]">Save Supplier</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment dialog */}
      <Dialog open={!!payDlg} onOpenChange={(v) => { if (!v) setPayDlg(null); }}>
        <DialogContent className="rounded-sm bg-card border-border max-w-md">
          <DialogHeader><DialogTitle className="font-display uppercase">Record Payment</DialogTitle></DialogHeader>
          {payDlg && (
            <div className="space-y-3">
              <div className="text-xs font-mono border border-border rounded-sm p-2 bg-secondary/40 space-y-0.5">
                <div className="flex justify-between"><span className="text-muted-foreground">Invoice</span><span>{payDlg.invoice_number || payDlg.txn_number}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Supplier</span><span>{payDlg.party_name}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Total</span><span>{inr(payDlg.total)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Paid</span><span className="text-green-500">{inr(payDlg.paid_amount || 0)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Outstanding</span><span className="text-primary">{inr(payDlg.outstanding || 0)}</span></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Amount *</Label>
                  <Input data-testid="pay-amount" type="number" value={payForm.amount} onChange={(e) => setPayForm(p => ({ ...p, amount: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Method</Label>
                  <Select value={payForm.method} onValueChange={(v) => setPayForm(p => ({ ...p, method: v }))}>
                    <SelectTrigger data-testid="pay-method" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="upi">UPI</SelectItem>
                      <SelectItem value="card">Card</SelectItem>
                      <SelectItem value="bank">Bank Transfer</SelectItem>
                      <SelectItem value="cheque">Cheque</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Paid On</Label>
                <Input data-testid="pay-date" type="date" value={payForm.paid_on} onChange={(e) => setPayForm(p => ({ ...p, paid_on: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Note</Label>
                <Input data-testid="pay-note" value={payForm.note} onChange={(e) => setPayForm(p => ({ ...p, note: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/>
              </div>
              {payments.length > 0 && (
                <div className="border-t border-border pt-2">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Previous Payments</div>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {payments.map(p => (
                      <div key={p.id} className="flex justify-between text-xs font-mono">
                        <span className="text-muted-foreground">{p.paid_on} · {p.method}</span>
                        <span>{inr(p.amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <Button data-testid="save-payment-btn" onClick={savePayment} disabled={!(Number(payForm.amount) > 0)} className="w-full rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em]">Save Payment</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
